import type { GanttTask } from "../types";
import { isArchivedGanttTask } from "./planningStatus";

/**
 * Pianificazione effort-driven del cronoprogramma.
 *
 * Ogni fase ha un carico di lavoro in ore (`oreStimate`). Il progetto ha una
 * capacità settimanale (`oreSettimana`) e una data di inizio cantiere.
 * La durata di ogni fase si calcola come effort / capacità giornaliera,
 * a meno che la fase non abbia una `durataFissaGiorni` (lavori di terzi o a
 * maturazione) che ha priorità e ignora la capacità.
 *
 * Le date rispettano le dipendenze (`dipendeDa`) e saltano sabato e domenica.
 */

export interface ScheduleOptions {
  /** Capacità settimanale in ore (ore/settimana). */
  oreSettimana: number;
  /** Data di inizio cantiere (ISO string o null). */
  dataInizioCantiere: string | null | undefined;
}

export interface ScheduledTask {
  dataInizio: string; // YYYY-MM-DD
  dataFine: string; // YYYY-MM-DD
  durataGiorni: number; // giorni lavorativi
}

export type ScheduleResult = Record<string, ScheduledTask>;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Ritorna true se la data è sabato o domenica. */
function isWeekend(d: Date): boolean {
  const day = d.getDay();
  return day === 0 || day === 6;
}

/** Normalizza a mezzanotte (parte data). */
function toMidnight(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Parsea una data YYYY-MM-DD (o ISO) a Date locale a mezzanotte. */
function parseDate(value: string): Date {
  // Prende solo la parte data per evitare shift di fuso orario.
  const [y, m, day] = value.split("T")[0].split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, day ?? 1);
}

/** Formatta una Date come YYYY-MM-DD (locale, senza shift UTC). */
export function formatISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Sposta la data al primo giorno lavorativo >= alla data data. */
function nextWorkingDay(d: Date): Date {
  const result = toMidnight(d);
  while (isWeekend(result)) {
    result.setDate(result.getDate() + 1);
  }
  return result;
}

/** Aggiunge N giorni lavorativi a partire da `start` (start è il giorno 1). */
function addWorkingDays(start: Date, workingDays: number): Date {
  // start deve già essere un giorno lavorativo; conta start come primo giorno.
  const result = toMidnight(start);
  let remaining = Math.max(1, workingDays) - 1;
  while (remaining > 0) {
    result.setDate(result.getDate() + 1);
    if (!isWeekend(result)) {
      remaining--;
    }
  }
  return result;
}

/** Primo giorno lavorativo successivo a `d` (strettamente dopo). */
function firstWorkingDayAfter(d: Date): Date {
  const result = toMidnight(d);
  do {
    result.setDate(result.getDate() + 1);
  } while (isWeekend(result));
  return result;
}

/** Durata in giorni lavorativi di una fase dato l'effort e la capacità. */
export function computeDurataGiorni(
  task: Pick<GanttTask, "oreStimate" | "durataFissaGiorni">,
  oreSettimana: number,
): number {
  if (task.durataFissaGiorni != null) {
    return Math.max(1, task.durataFissaGiorni);
  }
  const oreGiorno = oreSettimana > 0 ? oreSettimana / 5 : 0;
  if (oreGiorno <= 0) return 1;
  return Math.max(1, Math.ceil((task.oreStimate || 0) / oreGiorno));
}

/**
 * Calcola la pianificazione di tutti i task.
 * I task con `dipendeDa` iniziano il primo giorno lavorativo successivo alla
 * dataFine del predecessore; quelli senza dipendenza iniziano alla data di
 * inizio cantiere. Più task che dipendono dallo stesso predecessore partono in
 * parallelo dalla stessa data.
 */
export function computeSchedule(
  tasks: GanttTask[],
  options: ScheduleOptions,
): ScheduleResult {
  tasks = tasks.filter((task) => !isArchivedGanttTask(task));
  const { oreSettimana } = options;
  const result: ScheduleResult = {};

  if (tasks.length === 0) return result;

  // Data di inizio cantiere (fallback: primo giorno lavorativo da oggi).
  const startBase = options.dataInizioCantiere
    ? parseDate(options.dataInizioCantiere)
    : new Date();
  const cantiereStart = nextWorkingDay(startBase);

  const byId = new Map<string, GanttTask>();
  for (const t of tasks) byId.set(t.id, t);

  // Risoluzione ricorsiva con memoizzazione; protezione da cicli.
  const inProgress = new Set<string>();

  function resolve(task: GanttTask): ScheduledTask {
    const cached = result[task.id];
    if (cached) return cached;

    const durataGiorni = computeDurataGiorni(task, oreSettimana);

    let start: Date;
    const parentId = task.dipendeDa;
    // Il predecessore deve esistere ed essere diverso dal task stesso; in caso
    // di ciclo (già in risoluzione) si ignora la dipendenza.
    if (
      parentId &&
      byId.has(parentId) &&
      parentId !== task.id &&
      !inProgress.has(parentId)
    ) {
      inProgress.add(task.id);
      const parent = resolve(byId.get(parentId)!);
      inProgress.delete(task.id);
      start = firstWorkingDayAfter(parseDate(parent.dataFine));
    } else {
      start = nextWorkingDay(cantiereStart);
    }

    const end = addWorkingDays(start, durataGiorni);
    const scheduled: ScheduledTask = {
      dataInizio: formatISODate(start),
      dataFine: formatISODate(end),
      durataGiorni,
    };
    result[task.id] = scheduled;
    return scheduled;
  }

  for (const t of tasks) resolve(t);

  return result;
}

/** Conta i giorni lavorativi (inclusivi) tra due date. */
export function workingDaysBetween(start: Date, end: Date): number {
  let count = 0;
  const cur = toMidnight(start);
  const last = toMidnight(end);
  while (cur <= last) {
    if (!isWeekend(cur)) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

export interface ScheduleSummary {
  dataInizio: string | null;
  dataFine: string | null;
  giorniLavorativi: number;
  settimane: number;
}

/** Riepilogo complessivo della pianificazione (durata totale + data fine). */
export function summarizeSchedule(schedule: ScheduleResult): ScheduleSummary {
  const entries = Object.values(schedule);
  if (entries.length === 0) {
    return {
      dataInizio: null,
      dataFine: null,
      giorniLavorativi: 0,
      settimane: 0,
    };
  }

  let min = parseDate(entries[0].dataInizio);
  let max = parseDate(entries[0].dataFine);
  for (const e of entries) {
    const s = parseDate(e.dataInizio);
    const f = parseDate(e.dataFine);
    if (s < min) min = s;
    if (f > max) max = f;
  }

  const giorniLavorativi = workingDaysBetween(min, max);
  // Settimane basate sul calendario (arco temporale), coerenti con la capacità.
  const settimaneCalendario =
    Math.ceil(
      (toMidnight(max).getTime() - toMidnight(min).getTime()) / DAY_MS / 7,
    ) || 1;

  return {
    dataInizio: formatISODate(min),
    dataFine: formatISODate(max),
    giorniLavorativi,
    settimane: settimaneCalendario,
  };
}
